import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Calendar,
  CreditCard,
  Receipt,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  PieChart,
  BarChart3,
  Filter,
} from 'lucide-react';
import { financeiroService, ResumoFinanceiro } from '@/services/financeiroService';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart as RePieChart, Cell, Pie } from 'recharts';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEmpresaFinanceira } from '@/contexts/EmpresaFinanceiraContext';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

interface DashboardFinanceiroProps {
  periodo: string;
  mes?: number;
  ano?: number;
  dataInicio?: string;
  dataFim?: string;
  onNavigate?: (tab: string) => void;
}

export function DashboardFinanceiro({ periodo, mes, ano, dataInicio, dataFim, onNavigate }: DashboardFinanceiroProps) {
  const isMobile = useIsMobile();
  const { empresaId } = useEmpresaFinanceira();
  const [resumo, setResumo] = useState<ResumoFinanceiro | null>(null);
  const [resumoAnterior, setResumoAnterior] = useState<ResumoFinanceiro | null>(null);
  const [loading, setLoading] = useState(true);
  const [dadosGrafico, setDadosGrafico] = useState<any[]>([]);
  const [dadosCategorias, setDadosCategorias] = useState<any[]>([]);

  useEffect(() => {
    carregarDados();
  }, [periodo, mes, ano, dataInicio, dataFim, empresaId]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const hoje = new Date();
      let inicio: Date, fim: Date, inicioAnterior: Date, fimAnterior: Date;

      if (dataInicio && dataFim) {
        inicio = new Date(dataInicio);
        fim = new Date(dataFim);
        const dias = Math.ceil((fim.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24));
        inicioAnterior = new Date(inicio);
        inicioAnterior.setDate(inicioAnterior.getDate() - dias);
        fimAnterior = new Date(fim);
        fimAnterior.setDate(fimAnterior.getDate() - dias);
      } else {
        switch (periodo) {
          case 'mes_atual':
            inicio = startOfMonth(hoje);
            fim = endOfMonth(hoje);
            inicioAnterior = startOfMonth(subMonths(hoje, 1));
            fimAnterior = endOfMonth(subMonths(hoje, 1));
            break;
          case 'mes_especifico':
            if (mes !== undefined && ano !== undefined) {
              inicio = new Date(ano, mes, 1);
              fim = endOfMonth(inicio);
              inicioAnterior = startOfMonth(subMonths(inicio, 1));
              fimAnterior = endOfMonth(subMonths(inicio, 1));
            } else {
              inicio = startOfMonth(hoje);
              fim = endOfMonth(hoje);
              inicioAnterior = startOfMonth(subMonths(hoje, 1));
              fimAnterior = endOfMonth(subMonths(hoje, 1));
            }
            break;
          case 'ano_atual':
            inicio = new Date(hoje.getFullYear(), 0, 1);
            fim = new Date(hoje.getFullYear(), 11, 31);
            inicioAnterior = new Date(hoje.getFullYear() - 1, 0, 1);
            fimAnterior = new Date(hoje.getFullYear() - 1, 11, 31);
            break;
          case 'ultimo_semestre':
            inicio = startOfMonth(subMonths(hoje, 5));
            fim = endOfMonth(hoje);
            inicioAnterior = startOfMonth(subMonths(inicio, 6));
            fimAnterior = endOfMonth(subMonths(inicio, 1));
            break;
          case 'ultimos_12_meses':
            inicio = startOfMonth(subMonths(hoje, 11));
            fim = endOfMonth(hoje);
            inicioAnterior = startOfMonth(subMonths(inicio, 12));
            fimAnterior = endOfMonth(subMonths(inicio, 1));
            break;
          default:
            inicio = startOfMonth(hoje);
            fim = endOfMonth(hoje);
            inicioAnterior = startOfMonth(subMonths(hoje, 1));
            fimAnterior = endOfMonth(subMonths(hoje, 1));
        }
      }

      const [resumoAtual, resumoAnt, grafico, categorias] = await Promise.all([
        financeiroService.obterResumoFinanceiro(
          inicio.toISOString().split('T')[0],
          fim.toISOString().split('T')[0],
          empresaId
        ),
        financeiroService.obterResumoFinanceiro(
          inicioAnterior.toISOString().split('T')[0],
          fimAnterior.toISOString().split('T')[0],
          empresaId
        ),
        dataInicio && dataFim
          ? financeiroService.obterDadosGrafico('personalizado', inicio, {
              dataInicio: inicio,
              dataFim: fim,
            }, empresaId)
          : financeiroService.obterDadosGrafico(
              periodo === 'ano_atual' ? 'ano' : periodo === 'ultimos_12_meses' ? 'trimestre' : 'mes',
              inicio,
              undefined,
              empresaId
            ),
        financeiroService.obterDadosPorCategoria(
          'despesas',
          inicio.toISOString().split('T')[0],
          fim.toISOString().split('T')[0],
          empresaId
        )
      ]);

      setResumo(resumoAtual);
      setResumoAnterior(resumoAnt);
      setDadosGrafico(grafico);
      setDadosCategorias(categorias);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const calcularVariacao = (atual: number, anterior: number) => {
    if (anterior === 0) return atual > 0 ? 100 : 0;
    return ((atual - anterior) / anterior) * 100;
  };

  const formatarValor = (valor: number) => {
    return `R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatarValorCompacto = (valor: number) => {
    if (valor >= 1000000) {
      return `R$ ${(valor / 1000000).toFixed(2)}M`;
    }
    if (valor >= 1000) {
      return `R$ ${(valor / 1000).toFixed(2)}K`;
    }
    return formatarValor(valor);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const variacaoReceitas = calcularVariacao(
    resumo?.receitas_mes || 0,
    resumoAnterior?.receitas_mes || 0
  );
  const variacaoDespesas = calcularVariacao(
    resumo?.despesas_mes || 0,
    resumoAnterior?.despesas_mes || 0
  );
  const variacaoSaldo = calcularVariacao(
    resumo?.saldo_mes || 0,
    resumoAnterior?.saldo_mes || 0
  );

  const margemLucro = resumo?.receitas_mes
    ? ((resumo.saldo_mes / resumo.receitas_mes) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Resumo do Período Selecionado */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-muted-foreground bg-muted/50 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border border-border/50">
          <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
          <span className="text-xs sm:text-sm">
            {periodo === 'mes_especifico' && mes !== undefined && ano !== undefined
              ? format(new Date(ano, mes, 1), 'MMMM / yyyy', { locale: ptBR })
              : periodo === 'periodo_personalizado' && dataInicio && dataFim
              ? `${format(new Date(dataInicio), "MMM 'de' yyyy", { locale: ptBR })} — ${format(new Date(dataFim), "MMM 'de' yyyy", { locale: ptBR })}`
              : periodo === 'ano_atual'
              ? `Ano de ${new Date().getFullYear()}`
              : periodo === 'ultimo_semestre'
              ? 'Último Semestre'
              : periodo === 'ultimos_12_meses'
              ? 'Últimos 12 Meses'
              : format(new Date(), 'MMMM / yyyy', { locale: ptBR })}
          </span>
        </div>
        <div className="text-[10px] sm:text-xs text-muted-foreground italic">
          Comparando com período anterior equivalente
        </div>
      </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Receitas */}
        <Card className="border-none shadow-md bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 hover:shadow-lg transition-all duration-300">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs sm:text-sm font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                Receitas
              </CardTitle>
              <div className="p-1.5 sm:p-2 bg-emerald-500/20 rounded-xl shadow-inner">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <div className="space-y-1">
              <div className="text-xl sm:text-2xl font-black text-emerald-700 dark:text-emerald-400">
                {formatarValor(resumo?.receitas_mes || 0)}
              </div>
              <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold">
                {variacaoReceitas >= 0 ? (
                  <span className="flex items-center text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">
                    <ArrowUpRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5" />
                    {Math.abs(variacaoReceitas).toFixed(1)}%
                  </span>
                ) : (
                  <span className="flex items-center text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/30 px-1.5 py-0.5 rounded">
                    <ArrowDownRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5" />
                    {Math.abs(variacaoReceitas).toFixed(1)}%
                  </span>
                )}
                <span className="text-muted-foreground font-normal">vs anterior</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Despesas */}
        <Card className="border-none shadow-md bg-gradient-to-br from-rose-500/10 to-rose-500/5 hover:shadow-lg transition-all duration-300">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs sm:text-sm font-semibold text-rose-700 dark:text-rose-400 uppercase tracking-wider">
                Despesas
              </CardTitle>
              <div className="p-1.5 sm:p-2 bg-rose-500/20 rounded-xl shadow-inner">
                <TrendingDown className="h-4 w-4 sm:h-5 sm:w-5 text-rose-600" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <div className="space-y-1">
              <div className="text-xl sm:text-2xl font-black text-rose-700 dark:text-rose-400">
                {formatarValor(resumo?.despesas_mes || 0)}
              </div>
              <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold">
                {variacaoDespesas <= 0 ? (
                  <span className="flex items-center text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">
                    <ArrowDownRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5" />
                    {Math.abs(variacaoDespesas).toFixed(1)}%
                  </span>
                ) : (
                  <span className="flex items-center text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/30 px-1.5 py-0.5 rounded">
                    <ArrowUpRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5" />
                    {Math.abs(variacaoDespesas).toFixed(1)}%
                  </span>
                )}
                <span className="text-muted-foreground font-normal">vs anterior</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Saldo */}
        <Card className={`border-none shadow-md bg-gradient-to-br ${(resumo?.saldo_mes || 0) >= 0 ? 'from-sky-500/10 to-sky-500/5' : 'from-amber-500/10 to-amber-500/5'} hover:shadow-lg transition-all duration-300`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className={`text-xs sm:text-sm font-semibold uppercase tracking-wider ${(resumo?.saldo_mes || 0) >= 0 ? 'text-sky-700 dark:text-sky-400' : 'text-amber-700 dark:text-amber-400'}`}>
                Fluxo Líquido
              </CardTitle>
              <div className={`p-1.5 sm:p-2 rounded-xl shadow-inner ${(resumo?.saldo_mes || 0) >= 0 ? 'bg-sky-500/20' : 'bg-amber-500/20'}`}>
                <DollarSign className={`h-4 w-4 sm:h-5 sm:w-5 ${(resumo?.saldo_mes || 0) >= 0 ? 'text-sky-600' : 'text-amber-600'}`} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <div className="space-y-1">
              <div className={`text-xl sm:text-2xl font-black ${(resumo?.saldo_mes || 0) >= 0 ? 'text-sky-700 dark:text-sky-400' : 'text-amber-700 dark:text-amber-400'}`}>
                {formatarValor(resumo?.saldo_mes || 0)}
              </div>
              <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold">
                {variacaoSaldo >= 0 ? (
                  <span className="flex items-center text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">
                    <ArrowUpRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5" />
                    {Math.abs(variacaoSaldo).toFixed(1)}%
                  </span>
                ) : (
                  <span className="flex items-center text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/30 px-1.5 py-0.5 rounded">
                    <ArrowDownRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5" />
                    {Math.abs(variacaoSaldo).toFixed(1)}%
                  </span>
                )}
                <span className="text-muted-foreground font-normal">vs anterior</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Margem */}
        <Card className="border-none shadow-md bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 hover:shadow-lg transition-all duration-300">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs sm:text-sm font-semibold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">
                Margem Líquida
              </CardTitle>
              <div className="p-1.5 sm:p-2 bg-indigo-500/20 rounded-xl shadow-inner">
                <PieChart className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <div className="space-y-1">
              <div className="text-xl sm:text-2xl font-black text-indigo-700 dark:text-indigo-400">
                {margemLucro.toFixed(1)}%
              </div>
              <div className="text-[10px] sm:text-xs text-muted-foreground">
                Eficiência operacional do período
              </div>
              <Progress value={Math.max(0, Math.min(100, margemLucro))} className="h-1 sm:h-1.5 mt-2 bg-indigo-100 dark:bg-indigo-900/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fluxo de Caixa Temporal */}
        <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base sm:text-lg font-bold">Fluxo de Caixa</CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  {dataInicio && dataFim
                    ? 'Evolução financeira no período selecionado'
                    : 'Evolução financeira mensal'}
                </CardDescription>
              </div>
              <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className={`${isMobile ? 'h-[250px]' : 'h-[350px]'} w-full mt-2 sm:mt-4`}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart 
                  data={dadosGrafico} 
                  margin={{ 
                    top: 10, 
                    right: isMobile ? 5 : 10, 
                    left: isMobile ? -10 : 0, 
                    bottom: isMobile ? 0 : 0 
                  }}
                >
                  <defs>
                    <linearGradient id="colorReceitas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorDespesas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#888888" strokeOpacity={0.1} />
                  <XAxis 
                    dataKey="mes" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#888888', fontSize: isMobile ? 10 : 12 }}
                    dy={isMobile ? 5 : 10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#888888', fontSize: isMobile ? 10 : 12 }}
                    tickFormatter={(v) => `R$ ${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`}
                    width={isMobile ? 50 : 60}
                  />
                  <Tooltip 
                    cursor={{ stroke: '#888888', strokeOpacity: 0.1, strokeWidth: 2 }}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className={`bg-background/95 backdrop-blur-sm border border-border ${isMobile ? 'p-2' : 'p-3'} rounded-xl shadow-xl space-y-1 sm:space-y-2`}>
                            <p className={`font-bold ${isMobile ? 'text-xs' : 'text-sm'} border-b pb-1 mb-1`}>{label}</p>
                            {payload.map((entry: any, index: number) => (
                              <div key={index} className={`flex items-center justify-between gap-2 sm:gap-4 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                                <span className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                                  {entry.name}:
                                </span>
                                <span className="font-bold">{formatarValor(entry.value)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="receitas" 
                    stroke="#10b981" 
                    strokeWidth={isMobile ? 2 : 3}
                    fillOpacity={1}
                    fill="url(#colorReceitas)"
                    name="Receitas"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="despesas" 
                    stroke="#f43f5e" 
                    strokeWidth={isMobile ? 2 : 3}
                    fillOpacity={1}
                    fill="url(#colorDespesas)"
                    name="Despesas"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Despesas por Categoria */}
        <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base sm:text-lg font-bold">Distribuição de Gastos</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Principais categorias de despesas</CardDescription>
              </div>
              <Filter className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className={`grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2'} items-center gap-4`}>
              <div className={isMobile ? 'h-[200px]' : 'h-[300px]'}>
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Pie
                      data={dadosCategorias}
                      cx="50%"
                      cy="50%"
                      innerRadius={isMobile ? 40 : 60}
                      outerRadius={isMobile ? 60 : 80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {dadosCategorias.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(v: number) => formatarValor(v)}
                    />
                  </RePieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 sm:space-y-3">
                {dadosCategorias.slice(0, 5).map((item, index) => {
                  const percentual = (item.value / (resumo?.despesas_mes || 1)) * 100;
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] sm:text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                          <span className="font-medium truncate max-w-[100px] sm:max-w-[120px]">{item.name}</span>
                          <span className="text-muted-foreground text-[9px] sm:text-[10px]">({percentual.toFixed(1)}%)</span>
                        </div>
                        <span className="font-bold text-[10px] sm:text-xs">{formatarValor(item.value)}</span>
                      </div>
                      <Progress 
                        value={percentual} 
                        className="h-0.5 sm:h-1" 
                        style={{ backgroundColor: `${COLORS[index % COLORS.length]}20` }}
                      />
                    </div>
                  );
                })}
                {dadosCategorias.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-xs sm:text-sm italic">
                    Nenhuma despesa registrada no período
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grid Inferior - Pendências e Obras */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300 col-span-1 md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
              Monitor de Pendências
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Contas aguardando liquidação</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="p-3 sm:p-4 rounded-2xl bg-rose-500/5 border border-rose-500/10 space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 font-bold text-xs sm:text-sm">
                  <Receipt className="h-3 w-3 sm:h-4 sm:w-4" />
                  CONTAS A PAGAR
                </div>
                <div className="text-xl sm:text-2xl font-black text-rose-700 dark:text-rose-400">
                  {formatarValor(resumo?.contas_pagar_pendentes || 0)}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onNavigate?.('contas-pagar')}
                  className="w-full text-rose-600 hover:text-rose-700 hover:bg-rose-500/10 h-7 sm:h-8 text-[10px] sm:text-xs font-bold"
                >
                  VER DETALHES
                </Button>
              </div>

              <div className="p-3 sm:p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/10 space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-bold text-xs sm:text-sm">
                  <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
                  CONTAS A RECEBER
                </div>
                <div className="text-xl sm:text-2xl font-black text-emerald-700 dark:text-emerald-400">
                  {formatarValor(resumo?.contas_receber_pendentes || 0)}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onNavigate?.('contas-receber')}
                  className="w-full text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10 h-7 sm:h-8 text-[10px] sm:text-xs font-bold"
                >
                  VER DETALHES
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader>
            <CardTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <Target className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-500" />
              Contratos Ativos
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Volume total à receber</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col justify-center items-center py-4 sm:py-6 space-y-3 sm:space-y-4">
            <div className="text-2xl sm:text-3xl font-black text-indigo-700 dark:text-indigo-400">
              {formatarValor(resumo?.valor_contratos_aberto || 0)}
            </div>
            <div className="w-full space-y-2">
              <div className="flex items-center justify-between text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-tighter">
                <span>Progresso Global</span>
                <span>{((resumo?.valor_recebido_contratos_aberto || 0) / (resumo?.total_volume_contratos_aberto || 1) * 100).toFixed(0)}%</span>
              </div>
              <Progress value={(resumo?.valor_recebido_contratos_aberto || 0) / (resumo?.total_volume_contratos_aberto || 1) * 100} className="h-1.5 sm:h-2" />
            </div>
            <p className="text-[9px] sm:text-[10px] text-muted-foreground text-center">
              Total de valores pendentes de recebimento em contratos ativos
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}




